import { JSX }			from "preact";
import { useId }		from "preact/hooks";

import { Dom }			from "ts-base/web/dom";

import { EditorEdit }	from "@v3/geko/form/field";
import { FieldError }	from "@v3/geko/form/FieldError";
import { ChoiceList }	from "@v3/geko/form/choiceList";
import {
	RadioSchema,
	RadioModel,
	RadioState,
}	from "@v3/geko/form/radioFieldModel";

export type RadioFieldProps<T>	= {
	label:		string,
	missing:	string,
	invalid:	string,
	choices:	ChoiceList<T>,
	schema:		RadioSchema<T>,
	model:		RadioModel<T>,
	onEdit:		(edit:EditorEdit<RadioState<T>>)=>void,
};

export const RadioField	= <T,>({ label, missing, invalid, choices, schema, model, onEdit }:RadioFieldProps<T>):JSX.Element	=> {
	const uid	= useId();

	/*
	see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-labelledby
	why .field-label is not a label element here
	*/
	return (
		<div
			role="radiogroup"
			aria-labelledby={uid}
			class={Dom.classes(
				"FormField",
				"FormField--radio",
				schema.required			&& "FormField--required",
				schema.showError(model)	&& "FormField--invalid",
			)}
		>
			<div class="FormField-label input-label" id={uid}>{label}</div>
			<div class="FormField-editor">
				<button type="button" class="FormField-focusTarget FormField-focusSink"/>
				{
					choices.choices.map(choice =>
						// TODO form this only exists in RadioField and ColorField
						<div class="input-radio-wrapper">
							<input
								id={uid + "-" + choice.key}
								class="input-radio"
								type="radio"
								required={schema.required}
								value={choice.key}
								checked={model.value === choice.model}
								onChange={ () =>
									onEdit({ value: choice.model, touching: true })
								}
								autocomplete="off"
							/>
							<label class="input-radio-label" for={uid + "-" + choice.key}>{choice.label}</label>
						</div>
					)
				}
				{
					(schema.required || schema.restricted) &&
					<FieldError
						forId={uid}
						problem={schema.problem(model)}
						missing={missing}
						invalid={invalid}
					/>
				}
			</div>
		</div>
	);
};
